import React from 'react';
import ElementList from './views/ElementList';
import Model from '../../../../utils/model';
import { SINGLE_ELEMENT_VIEW, ELEMENT_LIST_VIEW, ELEMENT_TYPES, INITIAL_LIMIT } from './valuePickerUtils';
import { pathOr, prepend } from 'ramda';
import { bindMethods } from '../../../../utils/core';
import { connect } from 'react-redux';
import { updateValuePickerFilters, clearFilters } from '../../../../actions/reduxActions/valuePickerFilters';
import { getRecentValues } from '../../../../actions/reduxActions/recentValues';

import '../../../../../css/value-picker.less';
import '../../../../../css/picker.less';

const VIEWS = {
    ELEMENT_LIST_VIEW,
    SINGLE_ELEMENT_VIEW,
};

/**
 * @param {String} contentTypeFilter A contentType used for filtering requests to the value reference endpoint
 * @param {String} typeElementIdFilter A typeElementId used for filtering requests to the value reference endpoint
 * @param {String} elementType What type of element this selector is selecting e.g. 'value' or 'type' default: 'value'
 * @param {Function} renderOption Callback which returns a JSX snippet representing a single value/type
 * @param {Function} select Callback which accepts value/type metadata
 * @param {Function} getList Promise that returns a list of the elementType
 * @param {React.Component} renderSingleElement Component that renders a single element for creating/editing
 * @param {Function} close Close the modal
 * @description A component that lists values/types and allows for
 * creation and updating of primitive values and any type
 */

export class ValuePicker extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            view: this.props.elementToEdit ? VIEWS.SINGLE_ELEMENT_VIEW : VIEWS.ELEMENT_LIST_VIEW,
            visibleElements: [],
            elementToEdit: this.props.elementToEdit || null,
            errors: [],
            isLoading: true,
        };

        bindMethods(
            this,
            'setState',
            'switchView',
            'editElement',
            'fetchElements',
        );
    }

    componentDidMount() {
        this.props.updateValuePickerFilters('limit', INITIAL_LIMIT);
        if (this.props.contentTypeFilter) {
            this.props.updateValuePickerFilters('contentType', this.props.contentTypeFilter);
        }
        if (this.props.typeElementIdFilter) {
            this.props.updateValuePickerFilters('typeElementId', this.props.typeElementIdFilter);
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.valuePickerFilters !== this.props.valuePickerFilters) {
            this.fetchElements(this.props.valuePickerFilters);
            this.props.getRecentValues(this.props.currentUser.authenticationToken, this.props.currentUser.tenantId, this.props.valuePickerFilters);
        }
    }

    componentWillUnmount() {
        this.props.clearFilters();
        // We deliberately don't clear our recent values as they may be used in another tab
    }

    clearError = (index) => {
        const { errors } = this.state;
        errors.splice(index, 1);
        this.setState({ errors });
    };

    fetchElements(filters) {
        this.setState({ isLoading: true });
        const { authenticationToken, tenantId } = this.props.currentUser;

        this.props.getList(authenticationToken, tenantId, filters)
            .then(response => this.setState({ visibleElements: response.map(value => ({ ...value, key: Model.guid() })), isLoading: false }))
            .catch((error) => {
                const errors = prepend({ message: error.message }, this.state.errors);
                this.setState({ errors, isLoading: false });
            });
    }

    switchView(view) {
        this.setState({ view });
    }

    editElement(view, option = null) {
        this.setState({ view, elementToEdit: option });
    }

    render() {
        const activeView = this.state.view;

        return (
            <React.Fragment>
                {
                    activeView === VIEWS.ELEMENT_LIST_VIEW ?
                        (
                            <ElementList
                                close={this.props.close}
                                errors={this.state.errors.map(error => <div>{error.message}</div>)}
                                clearError={this.clearError}
                                elements={this.state.visibleElements}
                                renderOption={this.props.renderOption}
                                select={this.props.select}
                                switchView={this.editElement}
                                elementType={this.props.elementType || ELEMENT_TYPES.value}
                                isLoading={this.state.isLoading}
                                recentValues={this.props.recentValues}
                            />
                        )
                        : null
                }

                {
                    activeView === VIEWS.SINGLE_ELEMENT_VIEW ?
                        this.props.renderSingleElement(
                            {
                                elementId: pathOr(null, ['id'], this.state.elementToEdit),
                                switchView: this.switchView,
                                select: this.props.select,
                            },
                        )
                        : null
                }

            </React.Fragment>
        );
    }
}

const mapStateToProps = state => ({
    currentUser: state.currentUser,
    valuePickerFilters: state.valuePickerFilters,
    recentValues: state.recentValues,
});

const mapDispatchToProps = {
    updateValuePickerFilters,
    clearFilters,
    getRecentValues,
};

export default connect(mapStateToProps, mapDispatchToProps)(ValuePicker);
